import { supabase } from "@/lib/supabaseClient";

type Logger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
};

// Never retry here: a timeout may occur after a provider accepted the event.
export async function syncAuthenticatedSessionStart({
  userId,
  payload,
  fetchFn = fetch,
  logger,
}: {
  userId: string;
  payload: Record<string, unknown>;
  fetchFn?: typeof fetch;
  logger: Logger;
}): Promise<void> {
  try {
    const { data, error } = await supabase.auth.getSession();
    const session = data.session;
    if (error || !session?.access_token || session.user.id !== userId) {
      logger.warn("Lifecycle session sync skipped", { reason: "auth_session_unavailable" });
      return;
    }
    const response = await fetchFn("/api/loops/session", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
      redirect: "error",
    });
    if (!response.ok) {
      logger.warn("Lifecycle session sync request failed", { status: response.status });
      return;
    }
    const result = await response.json();
    if (result?.skipped === true) {
      // Do not log arbitrary response bodies or provider error text.
      logger.info("Lifecycle session sync skipped by server");
      return;
    }
    for (const provider of ["loops", "brevo"] as const) {
      for (const operation of ["contact", "event"] as const) {
        const outcome = result?.providers?.[provider]?.[operation];
        if (outcome?.ok !== true) {
          logger.warn("Lifecycle provider outcome unsuccessful or missing", {
            provider,
            operation,
          });
        } else if (outcome.skipped === true) {
          logger.info("Lifecycle provider operation skipped", { provider, operation });
        } else {
          logger.info("Lifecycle provider operation accepted", { provider, operation });
        }
      }
    }
  } catch {
    logger.warn("Lifecycle session sync failed", { reason: "transport_or_auth_error" });
  }
}
